import React, { useEffect } from 'react';
import styled from 'styled-components';
import Footer from './Footer';
import { colors } from '../styles/colors';
import Navigation from './Navigation';

const FullScreenContainer = styled.div`
  width: 100vw;
  min-width: 320px;
  min-height: 100vh;
  min-height: -webkit-fill-available;
  overflow-x: hidden;
  overscroll-behavior-x: none;
  position: relative;
  display: flex;
  flex-direction: column;
  background-color: ${colors.neutral.black};
`;

const HeaderSection = styled.div`
  width: 100%;
  min-height: 120px;
  background-color: ${colors.neutral.darkBrown};
  background-size:
    2px 2px, 3px 3px, 1px 1px, 4px 4px, 5px 5px, 6px 6px,
    2px 2px, 7px 7px, 1px 1px, 8px 8px, 2px 2px, 3px 3px;
  background-position:
    0 0, 1px 1px, 2px 2px, 3px 3px, 4px 4px, 5px 5px,
    6px 6px, 7px 7px, 8px 8px, 9px 9px, 10px 10px, 11px 11px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 40px 20px;
  box-sizing: border-box;
  position: relative;

  @media (max-width: 1240px) {
    padding: 40px 40px;
  }

  @media (max-width: 800px) {
    min-height: 280px;
    padding: 30px 20px;
  }

  @media (max-width: 480px) {
    min-height: 240px;
    padding: 25px 15px;
  }
`;

const PageTitle = styled.h1`
  font-family: 'saint-andrews-queen', cursive;
  font-size: clamp(7rem, 10vw, 8rem);
  color: ${colors.primary.gold};
  text-align: center;
  padding-top: 100px;
  margin: 0;
  letter-spacing: 2px;
  line-height: 1.2;

  @media (max-width: 800px) {
    font-size: clamp(4rem, 8vw, 6rem);
  }

  @media (max-width: 480px) {
    font-size: clamp(3rem, 6vw, 4.5rem);
  }
`;

const MountainBottom = styled.div`
  position: relative;
  width: 100%;
  height: auto;
  z-index: 50;
  margin-top: -40px; /* Pull mountain up to overlap with section above */
  overflow: hidden;

  img {
    width: 100%;
    height: auto;
    display: block;
    min-height: 80px;
    position: relative;
    top: -1px;
    margin-bottom: -1px;
    transform: scaleY(-1) scaleX(1.01); /* Flip upside down and scale slightly wider */
  }

  @media (max-width: 800px) {
    margin-top: -30px;
    img {
      min-height: 60px;
    }
  }

  @media (max-width: 480px) {
    margin-top: -20px;
    img {
      min-height: 40px;
      transform: scaleY(-1) scaleX(1.02);
    }
  }
`;

const ContentSection = styled.section<{ backgroundColor?: string }>`
  width: 100%;
  background-color: ${props => props.backgroundColor || colors.primary.lightTan};
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  box-sizing: border-box;
  position: relative;
  z-index: 10;
  min-height: 400px;
  /* Pull the section up under the mountain divider so the seam blends. The large
     top padding clears the overlap; the bottom only needs breathing room. */
  margin-top: -150px;
  padding: 230px 20px 90px;

  @media (max-width: 1240px) {
    padding: 200px 40px 80px;
  }

  @media (max-width: 800px) {
    min-height: 200px;
    margin-top: -80px;
    padding: 140px 15px 60px;
  }

  @media (max-width: 480px) {
    min-height: 150px;
    margin-top: -50px;
    padding: 110px 15px 50px;
  }
`;

const MessageContainer = styled.div`
  width: 100%;
  max-width: 900px;
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
`;

const MessageText = styled.p`
  font-family: 'GremeryTwo', cursive;
  font-size: clamp(1.3rem, 2.8vw, 1.8rem);
  color: ${colors.neutral.darkOlive};
  text-align: center;
  margin: 0;
  max-width: 800px;
  line-height: 1.6;
  font-style: italic;
  letter-spacing: 0.5px;

  @media (max-width: 768px) {
    font-size: 0.95rem;
  }

  @media (max-width: 480px) {
    font-size: 0.85rem;
  }
`;

const RegistryButtonWrapper = styled.a`
  display: inline-block;
  cursor: pointer;
  transition: opacity 0.3s ease;
  text-decoration: none;
  position: relative;
  width: 460px;
  margin-top: 50px;

  &:hover {
    opacity: 0.8;
  }

  @media (max-width: 768px) {
    width: 370px;
    margin-top: 38px;
  }

  @media (max-width: 480px) {
    width: 300px;
    margin-top: 32px;
  }
`;

const RegistryButtonImage = styled.img`
  width: 100%;
  height: auto;
  display: block;
`;

const RegistryButtonText = styled.div`
  position: absolute;
  top: 52%;
  left: 50%;
  transform: translate(-50%, -50%);
  color: ${colors.primary.lightTan};
  font-size: 1.45rem;
  font-family: 'GremeryOne', serif;
  font-weight: 500;
  letter-spacing: 1.5px;
  pointer-events: none;
  white-space: nowrap;

  @media (max-width: 768px) {
    font-size: 1.15rem;
    letter-spacing: 1px;
  }

  @media (max-width: 480px) {
    font-size: 0.95rem;
    letter-spacing: 0.5px;
  }
`;

const RegistryDetails: React.FC = () => {
  // Fix for mobile Safari viewport height
  useEffect(() => {
    const setVH = () => {
      const vh = window.innerHeight * 0.01;
      document.documentElement.style.setProperty('--vh', `${vh}px`);
    };

    setVH();
    window.addEventListener('resize', setVH);
    window.addEventListener('orientationchange', setVH);

    return () => {
      window.removeEventListener('resize', setVH);
      window.removeEventListener('orientationchange', setVH);
    };
  }, []);

  return (
    <FullScreenContainer>
      {/* Brown Header Section with Title */}
      <HeaderSection>
        <Navigation />
        <PageTitle>Registry</PageTitle>
      </HeaderSection>

      {/* Mountain divider from brown to beige */}
      <MountainBottom>
        <img src="/images/mountains/mountain-soft-top.svg" alt="Mountain divider" />
      </MountainBottom>

      {/* Beige Section - Registry */}
      <ContentSection backgroundColor={colors.primary.lightTan}>
        <MessageContainer>
          <MessageText>
            Your presence is the greatest gift of all. If you would like to celebrate with
            something more, our registry is hosted on Zola.
          </MessageText>
          <RegistryButtonWrapper
            href="https://www.zola.com/registry/emeryandgrant2026"
            target="_blank"
            rel="noopener noreferrer"
          >
            <RegistryButtonImage src="/images/svgs/funky-save.svg" alt="View Registry" />
            <RegistryButtonText>VIEW OUR REGISTRY</RegistryButtonText>
          </RegistryButtonWrapper>
        </MessageContainer>
      </ContentSection>

      <Footer />
    </FullScreenContainer>
  );
};

export default RegistryDetails;
